import { logger } from "@/helpers/loggerHelper";

type SSEListener = (_data: unknown) => void;

type SSEConnection = {
  eventSource: EventSource;
  listeners: SSEListener[];
};

class SSEService {
  private connections: Map<string, SSEConnection>;
  private retryInterval: number;
  private maxRetries: number;
  private retryCount: Map<string, number>;

  constructor() {
    this.connections = new Map();
    this.retryInterval = 10000;
    this.maxRetries = 5;
    this.retryCount = new Map();
  }

  connect(url: string, options: EventSourceInit = {}): SSEConnection {
    if (this.connections.has(url)) {
      logger.info(`SSE connection already exists for ${url}`);
      return this.connections.get(url) as SSEConnection;
    }
    const eventSource = new EventSource(url, options);
    const listeners: SSEListener[] = [];
    eventSource.onmessage = (event) => {
      listeners.forEach((listener) => listener(event.data));
    };
    eventSource.onerror = (err) => {
      logger.error(`SSE connection error for ${url}:`, err);
      const retries = this.retryCount.get(url) || 0;
      if (retries < this.maxRetries) {
        this.reconnect(url, options);
      } else {
        logger.error(
          `Max retries (${this.maxRetries}) reached for ${url}. Stopping reconnection.`
        );
        this.close(url);
      }
    };
    eventSource.onopen = () => {
      logger.info(`SSE connection established for ${url}`);
      this.retryCount.set(url, 0);
    };
    const connection = { eventSource, listeners };
    this.connections.set(url, connection);
    return connection;
  }

  addListener(url: string, callback: SSEListener): void {
    if (this.connections.has(url)) {
      this.connections.get(url)?.listeners.push(callback);
    } else {
      logger.error(`No connection found for ${url}`);
    }
  }

  reconnect(url: string, options: EventSourceInit): void {
    const retries = this.retryCount.get(url) || 0;
    this.retryCount.set(url, retries + 1);
    setTimeout(() => {
      logger.info(
        `Reconnecting to SSE at ${url}... (Attempt ${retries + 1}/${this.maxRetries})`
      );
      this.close(url);
      this.connect(url, options);
    }, this.retryInterval);
  }

  close(url: string): void {
    if (this.connections.has(url)) {
      this.connections.get(url)?.eventSource.close();
      this.connections.delete(url);
      this.retryCount.delete(url);
      logger.info(`SSE connection to ${url} closed`);
    }
  }

  closeAll(): void {
    this.connections.forEach((connection, url) => {
      connection.eventSource.close();
      this.connections.delete(url);
      this.retryCount.delete(url);
      logger.info(`SSE connection to ${url} closed`);
    });
  }
}

const sseService = new SSEService();
export default sseService;
